import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, Link } from 'react-router-dom';
import { ForgotPasswordPage, LoginPage, ResetPasswordPage } from '@ui/auth/AuthPages';
import { PublicOnly, Redirect, RequireAuth } from '@ui/auth/guards';
import { PageLoader } from '@ui/components/ui';
import { ADMIN_APP_URL } from '@ui/lib/urls';
import type { Session } from '@ui/store/types';
import { WebShell } from '@/components/layout/WebShell';
import { AcceptInvitePage, RegisterPage } from '@/features/auth/AuthPages';
import { useMeQuery } from '@/store/orgApi';

/** The customer app. The admin panel is a separate app (apps/admin) on its own domain. */

const page = <T extends Record<string, React.ComponentType>>(load: () => Promise<T>, name: keyof T) => {
  const Component = lazy(() => load().then((m) => ({ default: m[name] as React.ComponentType })));
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
};

/** Signed in without an organization: a platform admin belongs in the admin panel. */
const home = (session: Session): string => (session.organization || !session.user.isPlatformAdmin ? '/' : ADMIN_APP_URL);

function RequireOrg() {
  const { data } = useMeQuery();
  if (!data?.organization) return data?.user.isPlatformAdmin ? <Redirect to={ADMIN_APP_URL} /> : <Navigate to="/login" replace />;
  return <Outlet />;
}

/** Bookings pages exist only with the ERP module on. */
function RequireErp() {
  const { data } = useMeQuery();
  return data?.organization?.modules?.erp ? <Outlet /> : <Navigate to="/" replace />;
}

/** CRM pages exist only for organizations with the module on. */
function RequireCrm() {
  const { data } = useMeQuery();
  return data?.organization?.modules?.crm ? <Outlet /> : <Navigate to="/" replace />;
}

function SignUpLink() {
  const { data } = useMeQuery();
  if (data?.platform.allowSignup === false) return null;
  return <>New here? <Link to="/register" className="link">Create an account</Link></>;
}

export const router = createBrowserRouter([
  { path: '/login', element: <PublicOnly home={home}><LoginPage destination={home} footer={<SignUpLink />} /></PublicOnly> },
  { path: '/register', element: <PublicOnly home={home}><RegisterPage /></PublicOnly> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/accept-invite', element: <AcceptInvitePage /> },
  {
    path: '/',
    element: <RequireAuth><RequireOrg /></RequireAuth>,
    children: [
      {
        element: <WebShell />,
        children: [
          { index: true, element: page(() => import('@/features/dashboard/DashboardPage'), 'DashboardPage') },
          { path: 'agents', element: page(() => import('@/features/agents/AgentsPage'), 'AgentsPage') },
          { path: 'agents/:id', element: page(() => import('@/features/agents/AgentEditor'), 'AgentEditor') },
          { path: 'contacts', element: page(() => import('@/features/contacts/ContactsPage'), 'ContactsPage') },
          { path: 'contacts/import', element: page(() => import('@/features/contacts/ImportPage'), 'ImportPage') },
          { path: 'campaigns', element: page(() => import('@/features/campaigns/CampaignsPage'), 'CampaignsPage') },
          { path: 'campaigns/new', element: page(() => import('@/features/campaigns/CampaignForm'), 'CampaignForm') },
          { path: 'campaigns/:id', element: page(() => import('@/features/campaigns/CampaignDetail'), 'CampaignDetail') },
          { path: 'campaigns/:id/edit', element: page(() => import('@/features/campaigns/CampaignForm'), 'CampaignForm') },
          { path: 'calls', element: page(() => import('@/features/calls/CallsPage'), 'CallsPage') },
          { path: 'calls/:id', element: page(() => import('@/features/calls/CallDetail'), 'CallDetail') },
          { path: 'reports', element: page(() => import('@/features/reports/ReportsPage'), 'ReportsPage') },
          { path: 'messages', element: page(() => import('@/features/messages/MessagesPage'), 'MessagesPage') },
          { path: 'numbers', element: page(() => import('@/features/account/NumbersPage'), 'NumbersPage') },
          { path: 'billing', element: page(() => import('@/features/account/BillingPage'), 'BillingPage') },
          { path: 'team', element: page(() => import('@/features/account/AccountPages'), 'TeamPage') },
          { path: 'audit', element: page(() => import('@/features/account/AccountPages'), 'AuditPage') },
          { path: 'settings', element: page(() => import('@/features/account/AccountPages'), 'SettingsPage') },
          { path: 'settings/profile', element: page(() => import('@ui/account/ProfilePage'), 'ProfilePage') },
          {
            path: 'erp',
            element: <RequireErp />,
            children: [
              { index: true, element: <Navigate to="/erp/bookings" replace /> },
              { path: 'bookings', element: page(() => import('@/features/erp/BookingsPage'), 'BookingsPage') },
              { path: 'settings', element: page(() => import('@/features/erp/ErpSettingsPage'), 'ErpSettingsPage') },
              { path: 'invoices', element: page(() => import('@/features/erp/InvoicesPage'), 'InvoicesPage') }
            ]
          },
          {
            path: 'crm',
            element: <RequireCrm />,
            children: [
              { index: true, element: <Navigate to="/crm/pipeline" replace /> },
              { path: 'pipeline', element: page(() => import('@/features/crm/PipelinePage'), 'PipelinePage') },
              { path: 'tasks', element: page(() => import('@/features/crm/TasksPage'), 'TasksPage') },
              { path: 'settings', element: page(() => import('@/features/crm/CrmSettingsPage'), 'CrmSettingsPage') },
              { path: 'sources', element: page(() => import('@/features/crm/LeadSourcesPage'), 'LeadSourcesPage') },
              { path: 'leads', element: page(() => import('@/features/crm/LeadLogPage'), 'LeadLogPage') },
              { path: 'playbooks', element: page(() => import('@/features/crm/PlaybooksPage'), 'PlaybooksPage') },
              { path: 'automations', element: page(() => import('@/features/crm/AutomationsPage'), 'AutomationsPage') },
              { path: 'approvals', element: page(() => import('@/features/crm/ApprovalsPage'), 'ApprovalsPage') },
              { path: 'reports', element: <Navigate to="/reports?tab=business" replace /> }
            ]
          }
        ]
      }
    ]
  },
  { path: '*', element: <Navigate to="/" replace /> }
]);
